/**
 * Team Inbox — composer (UI Spec §7.4, Phase-1 scope).
 *
 * Phase 1 = TEXT send only, through the EXISTING `waSendManualReply`
 * callable (the same one the live Messages screen calls — no new backend
 * this dispatch). Window CLOSED → the input is REPLACED by the locked bar
 * ("the keyboard never opens on a dead input"); the template picker (S4)
 * is Phase 4. Voice record / canned "/" are Phases 2–4.
 *
 * M3.3 — multi-file attach: files queue into preview chips; text goes
 * with the first file; subsequent files send as attachment-only (WhatsApp
 * only supports one media object per message). WA constraint is safe — we
 * call onSend() for each file sequentially.
 *
 * Desktop: Enter sends · Shift+Enter newline (§7.4).
 */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../lib/auth';
import {
  attachAvailability,
  uploadMessageAttachment,
  type ApprovedTemplate,
  type AttachmentInput,
} from '../../../lib/messagesStore';
import type {
  CannedReply,
  InboxConversation,
  SessionState,
} from '../lib/types';
import { SendPicker } from './SendPicker';
import type { NameContext } from '../lib/sendPicker';
import { EmojiPicker } from '../../../components/EmojiPicker';

interface PendingFile {
  file: File;
  previewUrl: string;
}

export function Composer({
  session,
  replyFrom,
  sending,
  onSend,
  onSendTemplate,
  onSendCanned,
  approvedTemplates = [],
  canned = [],
  nameCtx,
  onFocusChange,
  conversation,
  onAttachError,
  initialPickerOpen,
}: {
  session: SessionState;
  /** Full +91 line this reply goes out from (reply-from-right-number). */
  replyFrom: string | null;
  sending: boolean;
  onSend: (text: string, attachment?: AttachmentInput) => Promise<boolean>;
  /** Send ONE approved template with ordered params (any time). */
  onSendTemplate?: (
    templateName: string,
    params: string[],
  ) => Promise<boolean>;
  /** Send ONE canned message (resolved plain text) — in-window only. */
  onSendCanned?: (text: string) => Promise<boolean>;
  /** Meta-approved templates allowed on this number (server-resolved). */
  approvedTemplates?: ApprovedTemplate[];
  /** Canned messages visible to this staff member (shared + own personal). */
  canned?: CannedReply[];
  /** Name hierarchy inputs (saved-contact first name + WA profile name). */
  nameCtx?: NameContext;
  /** Phase-2 claim heartbeat: composer focus/blur (R-C2 soft lock). */
  onFocusChange?: (focused: boolean) => void;
  conversation: InboxConversation | null;
  onAttachError?: (msg: string | null) => void;
  /** Phase 3b first-touch deep link (/messages?c=…&picker=1): open the send
   *  picker as soon as the composer mounts — the lead record's "Send first
   *  touch" lands here with the canned/template picker already up. */
  initialPickerOpen?: boolean;
}) {
  const { user } = useAuth();
  const [draft, setDraft] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  /** Insert an emoji at the caret (or append) and keep focus in the field. */
  function insertEmoji(emoji: string) {
    const el = textareaRef.current;
    if (!el) {
      setDraft((d) => d + emoji);
      return;
    }
    const start = el.selectionStart ?? draft.length;
    const end = el.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + emoji + draft.slice(end);
    setDraft(next);
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + emoji.length;
      el.setSelectionRange(caret, caret);
    });
  }
  const [attaching, setAttaching] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [pickerOpen, setPickerOpen] = useState(initialPickerOpen === true);

  // Recipient label + phone for the picker header.
  const recipientLabel =
    conversation?.name ||
    nameCtx?.contactFirst ||
    nameCtx?.waProfileName ||
    conversation?.phone ||
    'this contact';
  const recipientPhone = conversation?.phone || null;

  function renderSendPicker() {
    if (!pickerOpen) return null;
    return (
      <SendPicker
        recipientLabel={recipientLabel}
        recipientPhone={recipientPhone}
        windowOpen={session.open}
        canned={canned}
        templates={approvedTemplates}
        nameCtx={nameCtx || {}}
        sending={sending}
        onSendCanned={onSendCanned || (async () => false)}
        onSendTemplate={onSendTemplate || (async () => false)}
        onClose={() => setPickerOpen(false)}
      />
    );
  }

  // Revoke object URLs on unmount to avoid memory leaks.
  useEffect(() => {
    return () => {
      for (const pf of pendingFiles) URL.revokeObjectURL(pf.previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function removePending(idx: number) {
    setPendingFiles((prev) => {
      URL.revokeObjectURL(prev[idx].previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  }

  async function submit() {
    const text = draft.trim();
    const hasFiles = pendingFiles.length > 0;
    if ((!text && !hasFiles) || sending || attaching) return;

    if (!hasFiles) {
      const ok = await onSend(text);
      if (ok) setDraft('');
      return;
    }

    setAttaching(true);
    onAttachError?.(null);
    try {
      const queue = pendingFiles;
      let firstSent = false;
      let failedIdx = -1;
      for (let i = 0; i < queue.length; i++) {
        const pf = queue[i];
        const attachment = await uploadMessageAttachment(
          conversation?.projectId || 'triage',
          pf.file,
          { uid: user?.uid || 'unknown', name: user?.displayName || user?.email || 'Team' },
        );
        // Text goes with the first file only (WA = one media per message).
        const ok = await onSend(firstSent ? '' : text, attachment);
        if (!ok) {
          // The send returned false WITHOUT throwing (a silent server
          // refusal). Stop here, surface a plain-language error and KEEP the
          // not-yet-sent chips so the staff member can retry — never clear
          // the queue on a failure.
          failedIdx = i;
          break;
        }
        firstSent = true;
      }
      if (failedIdx === -1) {
        // Every file went out — revoke previews and clear the queue + draft.
        for (const pf of queue) URL.revokeObjectURL(pf.previewUrl);
        setPendingFiles([]);
        setDraft('');
      } else {
        // Drop the chips that DID go out (revoke their previews); keep the
        // failed one and everything after it queued for a retry.
        for (let i = 0; i < failedIdx; i++) URL.revokeObjectURL(queue[i].previewUrl);
        setPendingFiles(queue.slice(failedIdx));
        // The caption rides the first file. If a LATER file failed the text
        // already went out → clear it; if the FIRST file failed the text did
        // not send → keep the draft so it retries with the file.
        if (firstSent) setDraft('');
        onAttachError?.(
          'The file did not go out — it was uploaded but WhatsApp did not ' +
          'accept it as a message. It is still attached below; please try ' +
          'sending again.',
        );
      }
    } catch (err) {
      const msg = (err as { message?: string }).message || 'Attach failed. Try again.';
      onAttachError?.(
        /send_blocked:window_closed_media_blocked/.test(msg)
          ? 'The 24-hour window closed while sending — a file cannot be ' +
            'sent outside it. Try again once the client messages.'
          : /unsupported_media_type/.test(msg)
            ? "WhatsApp can't send this file type (e.g. SVG/EPS). Please " +
              'convert it to PDF or PNG and try again.'
            : /not an accepted file type|the limit is/.test(msg)
              ? msg
              : 'The file did not go out. It was uploaded but not delivered ' +
                'as a WhatsApp message — try again.',
      );
    } finally {
      setAttaching(false);
    }
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (e.target) e.target.value = '';
    if (!files || !conversation) return;

    const avail = attachAvailability(conversation, session);
    if (!avail.ok) {
      onAttachError?.(avail.reason);
      return;
    }
    onAttachError?.(null);

    const newEntries: PendingFile[] = Array.from(files).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPendingFiles((prev) => [...prev, ...newEntries]);
  }

  if (!session.open) {
    return (
      <>
        <div className="ti-composer-locked" data-testid="ti-locked-bar">
          <span>
            Free typing is blocked outside the 24-hour window — WhatsApp
            allows approved templates only.
          </span>
          <button
            type="button"
            className="ti-tpl-open"
            disabled={sending || !onSendTemplate}
            onClick={() => setPickerOpen(true)}
            title="Send an approved WhatsApp template"
          >
            Send a message…
          </button>
        </div>
        {renderSendPicker()}
      </>
    );
  }

  const avail = attachAvailability(conversation, session);

  return (
    <>
      {replyFrom && (
        <div className="ti-replyfrom" aria-live="polite">
          Replying from <b>{replyFrom}</b>
        </div>
      )}
      {pendingFiles.length > 0 && (
        <div className="ti-attach-queue" aria-label="Pending attachments">
          {pendingFiles.map((pf, idx) => (
            <span key={idx} className="ti-attach-chip">
              {pf.file.type.startsWith('image/')
                ? <img src={pf.previewUrl} alt={pf.file.name} className="ti-attach-thumb" />
                : <span className="ti-attach-name">📎 {pf.file.name}</span>}
              <button
                type="button"
                className="ti-attach-chip-remove"
                aria-label={`Remove ${pf.file.name}`}
                onClick={() => removePending(idx)}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="ti-composer">
        <input
          type="file"
          ref={fileRef}
          style={{ display: 'none' }}
          // Pre-filter the OS picker to the types WhatsApp + the Storage rule
          // accept, so staff never pick a file only to have it rejected.
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
          onChange={onPickFile}
          disabled={sending || attaching}
          multiple
        />
        <button
          type="button"
          className="ti-attach"
          title={avail.ok ? 'Attach a file' : avail.reason}
          aria-label="Attach files"
          disabled={sending || attaching || !avail.ok}
          onClick={() => fileRef.current?.click()}
        >
          {attaching ? '…' : '📎'}
        </button>
        <button
          type="button"
          className="ti-sp-open"
          title="Send a canned message or an approved template"
          aria-label="Send a message"
          disabled={sending || attaching}
          onClick={() => setPickerOpen(true)}
        >
          ⚡
        </button>
        <EmojiPicker
          onPick={insertEmoji}
          ariaLabel="Insert emoji"
          disabled={sending || attaching}
        />
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => onFocusChange?.(true)}
          onBlur={() => onFocusChange?.(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder={attaching ? 'Sending…' : pendingFiles.length > 0 ? 'Add a caption…' : 'Type a message…'}
          aria-label="Reply message"
          rows={1}
          disabled={sending || attaching}
        />
        <button
          type="button"
          className="ti-send"
          disabled={sending || attaching || (!draft.trim() && pendingFiles.length === 0)}
          onClick={() => void submit()}
          aria-label="Send message"
          title="Send"
        >
          {sending || attaching ? '…' : '➤'}
        </button>
      </div>
      {renderSendPicker()}
    </>
  );
}
